import { CalloutV2, ErrorComponent, Page } from '@app-builder/components';
import { BreadCrumbLink, type BreadCrumbProps, BreadCrumbs } from '@app-builder/components/Breadcrumbs';
import { AddListValueModal } from '@app-builder/components/Lists/AddListValueModal';
import { DeleteListModal } from '@app-builder/components/Lists/DeleteListModal';
import { DeleteListValueModal } from '@app-builder/components/Lists/DeleteListValueModal';
import { EditListModal } from '@app-builder/components/Lists/EditListModal';
import { LoadingIcon } from '@app-builder/components/Spinner';
import { useLoaderRevalidator } from '@app-builder/contexts/LoaderRevalidatorContext';
import { authMiddleware } from '@app-builder/middlewares/auth-middleware';
import { useUploadListDataFile } from '@app-builder/queries/upload-list-data';
import {
  isCreateListValueAvailable,
  isDeleteListAvailable,
  isDeleteListValueAvailable,
  isEditListAvailable,
} from '@app-builder/services/feature-access';
import useAsync from '@app-builder/utils/hooks/use-async';
import { REQUEST_TIMEOUT } from '@app-builder/utils/http/http-status-codes';
import { parseIdParamSafe } from '@app-builder/utils/input-validation';
import { fromUUIDtoSUUID } from '@app-builder/utils/short-uuid';
import * as Sentry from '@sentry/react';
import { ClientOnly, createFileRoute, Link } from '@tanstack/react-router';
import { createServerFn } from '@tanstack/react-start';
import { createColumnHelper } from '@tanstack/react-table';
import clsx from 'clsx';
import { useMemo, useReducer, useState } from 'react';
import { useDropzone } from 'react-dropzone-esm';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import type { MarbleTableFeatures } from 'ui-design-system';
import { Button, CtaV2ClassName, Modal, SearchInput, Table, useVirtualTable } from 'ui-design-system';
import { Icon } from 'ui-icons';
import { z } from 'zod/v4';

const listLoader = createServerFn()
  .middleware([authMiddleware])
  .validator((input: { params?: Record<string, string> } | undefined) => input)
  .handler(async function listLoader({ context, data }) {
    const { user, customListsRepository } = context.authInfo;

    const parsedParams = await parseIdParamSafe(data?.params ?? {}, 'listId');
    if (!parsedParams.success) {
      throw new Response(null, { status: 404, statusText: 'Not Found' });
    }
    const customList = await customListsRepository.getCustomList(parsedParams.data.listId);

    return {
      customList: customList,
      listFeatureAccess: {
        isCreateListValueAvailable: isCreateListValueAvailable(user),
        isDeleteListValueAvailable: isDeleteListValueAvailable(user),
        isEditListAvailable: isEditListAvailable(user),
        isDeleteListAvailable: isDeleteListAvailable(user),
      },
    };
  });

type CustomListValue = {
  id: string;
  value: string;
};

const columnHelper = createColumnHelper<MarbleTableFeatures, CustomListValue>();

export const Route = createFileRoute('/_app/_builder/detection/lists/$listId')({
  loader: ({ params }) => listLoader({ data: { params } }),
  staticData: {
    BreadCrumbs: [
      ({ isLast }: BreadCrumbProps) => {
        const { customList } = Route.useLoaderData();

        return (
          <BreadCrumbLink
            to="/detection/lists/$listId"
            params={{ listId: fromUUIDtoSUUID(customList.id) }}
            isLast={isLast}
          >
            {customList.name}
          </BreadCrumbLink>
        );
      },
    ],
  },
  errorComponent: ({ error }) => {
    Sentry.captureException(error);
    return <ErrorComponent error={error} />;
  },
  component: Lists,
});

function Lists() {
  const { customList, listFeatureAccess } = Route.useLoaderData();
  const listValues = customList.values ?? [];
  const { t } = useTranslation(['lists', 'common']);
  const [searchValue, setSearchValue] = useState('');

  const columns = useMemo(
    () => [
      columnHelper.accessor((row) => row.value, {
        id: 'value',
        header: t('lists:detail.values-list.header'),
        size: 500,
        sortFn: 'text',
        enableSorting: true,
        cell: ({ getValue, row }) => {
          const value = getValue();
          return (
            <div className="group flex items-center justify-between">
              <p className="text-grey-primary text-s font-medium">{value}</p>
              {listFeatureAccess.isDeleteListValueAvailable ? (
                <DeleteListValueModal listId={customList.id} listValueId={row.original.id} value={value}>
                  <button
                    data-test="delete-list-value-trigger"
                    className="group-hover:text-grey-primary text-transparent transition-colors duration-200 ease-in-out"
                    name="delete"
                    tabIndex={-1}
                  >
                    <Icon icon="delete" className="size-6 shrink-0" />
                    <span className="sr-only">{t('common:delete')}</span>
                  </button>
                </DeleteListValueModal>
              ) : null}
            </div>
          );
        },
      }),
    ],
    [t, listFeatureAccess.isDeleteListValueAvailable, customList.id],
  );

  const virtualTable = useVirtualTable({
    data: listValues,
    columns,
    columnResizeMode: 'onChange',

    manualFiltering: false,
    manualSorting: false,
  });
  return (
    <Page.Main>
      <Page.Header className="justify-between">
        <BreadCrumbs />
        {listFeatureAccess.isEditListAvailable ? (
          <EditListModal listId={customList.id} name={customList.name} description={customList.description} />
        ) : null}
      </Page.Header>
      <Page.Container>
        <Page.Content width="table">
          {customList.description ? <CalloutV2>{customList.description}</CalloutV2> : null}
          {listValues.length > 0 ? <DownloadAsCSV listId={customList.id} /> : null}
          <UploadAsCsv listId={customList.id} />
          <div className="flex flex-col gap-sm overflow-hidden lg:gap-md">
            <div className="flex flex-row gap-sm lg:gap-md">
              <form className="flex grow items-center">
                <SearchInput
                  size="medium"
                  className="w-100"
                  disabled={listValues.length === 0}
                  aria-label={t('common:search')}
                  placeholder={t('common:search')}
                  value={searchValue}
                  onChange={(value) => {
                    setSearchValue(value);
                    virtualTable.table.setGlobalFilter(value);
                  }}
                />
              </form>
              {listFeatureAccess.isCreateListValueAvailable ? (
                <AddListValueModal listId={customList.id} kind={customList.kind} />
              ) : null}
            </div>
            {virtualTable.isEmpty ? (
              <div className="bg-surface-card border-grey-border flex h-28 flex-col items-center justify-center rounded-lg border border-solid p-md">
                <p className="text-s font-medium">
                  {listValues.length > 0
                    ? t('lists:empty_custom_list_matches')
                    : t('lists:empty_custom_list_values_list')}
                </p>
              </div>
            ) : (
              <Table.Default {...virtualTable}></Table.Default>
            )}
          </div>
          {listFeatureAccess.isDeleteListAvailable ? <DeleteListModal listId={customList.id} /> : null}
        </Page.Content>
      </Page.Container>
    </Page.Main>
  );
}

function DownloadAsCSV({ listId }: { listId: string }) {
  const { t } = useTranslation(['lists', 'common']);
  const downloadUrl = `/ressources/lists/download-csv-file/${fromUUIDtoSUUID(listId)}`;

  return (
    <Link reloadDocument to={downloadUrl} className={CtaV2ClassName({ variant: 'secondary', className: 'w-fit' })}>
      <Icon icon="download" className="size-6" />
      {t('lists:download_values_as_csv')}
    </Link>
  );
}

const UploadAsCsvDropzone = function UploadAsCsvDropzone({
  ref,
  className,
  ...props
}: React.ComponentPropsWithoutRef<'div'> & { ref?: React.Ref<HTMLDivElement> }) {
  return (
    <div
      ref={ref}
      className={clsx(
        'text-s flex h-40 flex-col items-center justify-center gap-md rounded-sm border-2 border-dashed',
        className,
      )}
      {...props}
    />
  );
};

type State =
  | {
      open: boolean;
      success: false;
      message: string;
    }
  | {
      open: boolean;
      success: true;
      total: {
        existing: number;
        deleted: number;
        created: number;
      };
    };
type Actions =
  | {
      type: 'success';
      total: {
        existing: number;
        deleted: number;
        created: number;
      };
    }
  | {
      type: 'error';
      message: string;
    }
  | {
      type: 'setOpen';
      open: boolean;
    };

const initialState: State = {
  open: false,
  message: '',
  success: false,
};

function modalReducer(state: State, action: Actions): State {
  switch (action.type) {
    case 'success':
      return {
        open: true,
        success: true,
        total: action.total,
      };
    case 'error':
      return {
        open: true,
        success: false,
        message: action.message,
      };
    case 'setOpen':
      return {
        ...state,
        open: action.open,
      };
    default:
      return state;
  }
}

function ClientUploadAsCsv({ listId }: { listId: string }) {
  const { t } = useTranslation(['lists', 'common']);
  const [modalState, dispatch] = useReducer(modalReducer, initialState);
  const uploadListDataFile = useUploadListDataFile(listId);
  const revalidate = useLoaderRevalidator();

  const [onDrop, { loading }] = useAsync(async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) {
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await uploadListDataFile.mutateAsync(formData);

      if (!response.ok) {
        if (response.status === REQUEST_TIMEOUT) {
          toast.error(t('lists:errors.request_timeout'));
          return;
        }
        let errorMessage: string | undefined;
        try {
          const errorResponse = z
            .object({
              message: z.string(),
            })
            .parse(await response.json());
          errorMessage = errorResponse.message;
        } catch (_error) {
          errorMessage = (await response.text()).trim();
        }

        dispatch({
          type: 'error',
          message:
            t('lists:errors.failure_message', {
              replace: { errorMessage },
            }) ?? t('common:global_error'),
        });
        return;
      }

      const { results } = z
        .object({
          results: z.object({
            total_existing: z.number(),
            total_deleted: z.number(),
            total_created: z.number(),
          }),
        })
        .parse(await response.json());

      dispatch({
        type: 'success',
        total: {
          existing: results.total_existing,
          deleted: results.total_deleted,
          created: results.total_created,
        },
      });
      revalidate();
    } catch (error) {
      dispatch({
        type: 'error',
        message: error instanceof Error ? error.message : t('common:global_error'),
      });
    }
  });

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles) => {
      void onDrop(acceptedFiles);
    },
    accept: { 'text/*': ['.csv'] },
    multiple: false,
  });

  return (
    <UploadAsCsvDropzone
      {...getRootProps()}
      className={isDragActive ? 'bg-purple-background border-purple-disabled opacity-90' : 'border-grey-placeholder'}
    >
      <input {...getInputProps()} />
      <p>{t('lists:drop_csv_here')}</p>
      <p className="text-grey-secondary uppercase">{t('common:or')}</p>
      <Button variant="primary">
        <LoadingIcon icon="upload" loading={loading} className="size-5" />
        {t('lists:pick_csv')}
      </Button>

      <Modal.Root
        open={modalState.open}
        onOpenChange={(open) => {
          dispatch({ type: 'setOpen', open });
        }}
      >
        <Modal.Content onClick={(e) => e.stopPropagation()}>
          <div className="bg-surface-card text-s flex flex-col items-center gap-lg p-lg">
            <Icon
              icon={modalState.success ? 'tick' : 'cross'}
              className={clsx(
                'size-[108px] rounded-full border-8',
                modalState.success
                  ? 'bg-purple-background border-transparent text-purple-primary'
                  : 'bg-red-background border-transparent text-red-primary',
              )}
            />
            <div className="flex flex-col items-center gap-sm text-center">
              <p className="text-l font-semibold">{t('lists:upload_csv.results')}</p>
              {modalState.success ? (
                <div className="flex flex-col items-start">
                  <p>{t('lists:upload_csv.success')}</p>
                  <ul className="list-inside list-disc text-start">
                    <li>
                      {t('lists:upload_csv.success.existing', {
                        count: modalState.total.existing,
                      })}
                    </li>
                    <li>
                      {t('lists:upload_csv.success.deleted', {
                        count: modalState.total.deleted,
                      })}
                    </li>
                    <li>
                      {t('lists:upload_csv.success.created', {
                        count: modalState.total.created,
                      })}
                    </li>
                  </ul>
                </div>
              ) : (
                <p>{modalState.message}</p>
              )}
            </div>
          </div>
          <Modal.Footer>
            <Modal.FooterButton isCloseButton variant="primary" label={t('common:understand')} leadingIcon="tick" />
          </Modal.Footer>
        </Modal.Content>
      </Modal.Root>
    </UploadAsCsvDropzone>
  );
}

function UploadAsCsv({ listId }: { listId: string }) {
  return (
    <ClientOnly
      fallback={
        <UploadAsCsvDropzone className="border-grey-placeholder">
          <LoadingIcon icon="upload" loading={true} className="size-6" />
        </UploadAsCsvDropzone>
      }
    >
      <ClientUploadAsCsv listId={listId} />
    </ClientOnly>
  );
}
